import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionExpiryStore } from '@/stores/sessionExpiryStore';

/** Show the reminder once the session has this many ms (or fewer) left. */
export const SESSION_WARN_BEFORE_MS = 2 * 60 * 1000; // 2 minutes

interface SessionExpiryReminder {
  /** True while the "expiring soon" reminder should be shown. */
  open: boolean;
  /** Ms remaining until idle-expiry (null when unknown). */
  remainingMs: number | null;
  /** Hide the reminder without extending (e.g. user chose Log out). */
  dismiss: () => void;
}

/**
 * Drives the proactive "session expiring soon" reminder off the live
 * `Session-Expires-In` value in the session-expiry store.
 *
 * - Ticks every second while enabled and an expiry is known.
 * - Opens the reminder inside the warning window (default 2 min before expiry).
 * - Auto-closes when activity pushes the expiry forward (store updates → recompute).
 * - Calls `onExpire` exactly once when the countdown reaches zero, so the app can
 *   surface the hard expiry immediately instead of waiting for the next API 401.
 *
 * `enabled` should be the authoritative auth-ready state (e.g. `!isLoading &&
 * isAuthenticated`) so it never runs for a logged-out user.
 */
export const useSessionExpiryReminder = (
  enabled: boolean,
  onExpire: () => void,
): SessionExpiryReminder => {
  const expiresAt = useSessionExpiryStore((s) => s.expiresAt);
  const [open, setOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Stable ref so the ticking effect doesn't restart when onExpire identity changes
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  // Guard so onExpire fires only once per expiry window
  const expiredFiredRef = useRef(false);

  // Re-arm the dismiss/expire guards whenever the expiry moves (i.e. new activity)
  useEffect(() => {
    setDismissed(false);
    expiredFiredRef.current = false;
  }, [expiresAt]);

  useEffect(() => {
    if (!enabled || expiresAt == null) {
      setOpen(false);
      setRemainingMs(null);
      return;
    }

    const tick = () => {
      const rem = expiresAt - Date.now();
      setRemainingMs(rem);

      if (rem <= 0) {
        setOpen(false);
        if (!expiredFiredRef.current) {
          expiredFiredRef.current = true;
          onExpireRef.current();
        }
        return;
      }

      setOpen(rem <= SESSION_WARN_BEFORE_MS && !dismissed);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [enabled, expiresAt, dismissed]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setOpen(false);
  }, []);

  return { open, remainingMs, dismiss };
};

export default useSessionExpiryReminder;
